/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import { assoc } from 'lodash/fp';
import { combineReducers } from 'redux';
import { moveItem } from '../../helpers';
import * as actions from '../actions/action-types';
import { regenerateUniqueKeys } from '../actions/builder';

const treeIntoFlags = (tree) => {
  return Object.keys(tree).reduce((badge, key) => {
    badge[key] = true; // Set Each Badge in the tree as to true ex: { 530138-Grid(pin):true }
    return badge;
  }, {});
};

// Remove a child component from its parent's children array
const removeChild = (componentId, parentId, tree) => {
  if (tree[parentId]) {
    tree[parentId].children = tree[parentId].children.filter(
      (childId) => childId !== componentId
    );
  }
  return tree;
};

// Delete a component from the tree
const deleteComponent = (componentId, tree) => {
  const parentId = tree[componentId]?.parentId;
  const updatedTree = { ...tree };

  // Remove the component itself
  delete updatedTree[componentId];

  // Remove the component from its parent's children
  if (parentId) {
    removeChild(componentId, parentId, updatedTree);
  }

  return updatedTree;
};

const moveComponent = ({ movingId, siblingId }, state) => {
  const oldParentId = state[movingId]?.parentId;
  const newParentId = state[siblingId]?.parentId;

  // Create a shallow copy of the state
  const updatedState = { ...state };

  // Update the parentId of the moving component
  updatedState[movingId] = { ...updatedState[movingId], parentId: newParentId };

  // Remove the moving component from its old parent's children
  if (oldParentId && updatedState[oldParentId]?.children) {
    updatedState[oldParentId].children = updatedState[
      oldParentId
    ].children.filter((child) => child !== movingId);
  }

  // Add the moving component to its new parent's children
  const newParentChildren = updatedState[newParentId]?.children || [];
  const siblingIndex = newParentChildren.findIndex((id) => siblingId === id);
  const updatedChildren = moveItem(
    [...newParentChildren, movingId], // Concatenate the moving component
    newParentChildren.length, // Original length before adding the new child
    siblingIndex // Find the index of the siblingId to insert the moving component
  );

  updatedState[newParentId].children = updatedChildren;

  return updatedState;
};

export const flatOutline = (state = {}, action) => {
  const { component, order = Infinity, type, id } = action;

  switch (type) {
    case actions.OUTLINE_RECEIVED:
      return {
        ...state,
        ...action.outline,
      };
    case actions.COMPONENT_UPDATED:
      return {
        ...state,
        [component.id]: component,
      };
    case actions.COMPONENT_DELETED:
      return deleteComponent(id, state);
    case actions.COMPONENT_ADDED:
      return {
        ...state,

        // add component
        [component.id]: {
          ...component,
          props: { ...component.props, untouched: true },
        },

        // inform parent
        [component.parentId]: {
          ...state[component.parentId],

          // splice in new component's id
          children: [
            ...(state[component?.parentId]?.children || []).slice(0, order),
            component.id,
            ...(state[component?.parentId]?.children || []).slice(order),
          ],
        },
      };
    case actions.ADD_REPEATABLE:
      return assoc(
        action.path,
        [
          ..._.get(state, action.path, []),
          { $unique_key: new Date().getTime(), ...action.repeatable },
        ],
        state
      );
    case actions.MOVE_REPEATABLE:
      return assoc(
        action.path,
        moveItem(
          _.get(state, action.path, []),
          action.fromIndex,
          action.toIndex
        ),
        state
      );
    case actions.DELETE_REPEATABLE:
      return assoc(
        action.path,
        _.get(state, action.path).filter((x, i) => i !== action.index),
        state
      );
    case actions.DUPLICATE_REPEATABLE: {
      const [parentKey, propsKey, itemsKey] = action.path;
      const { index } = action;

      const parent = state[parentKey];
      const props = parent[propsKey];
      const items = props[itemsKey];
      const item = items[index];

      return {
        ...state,
        [parentKey]: {
          ...parent,
          [propsKey]: {
            ...props,
            [itemsKey]: [...items, regenerateUniqueKeys(_.cloneDeep(item))],
          },
        },
      };
    }

    case actions.DUPLICATE_GRID_ITEM_COMPONENT: {
      const { path, index } = action;
      const { parentKey, propsKey, gridItemsKey, gridItemIndexKey } = path;

      const parent = state[parentKey];
      const props = parent[propsKey];
      const gridItems = props[gridItemsKey];

      const selectedItemComponent =
        gridItems?.[gridItemIndexKey]?.itemComponents?.[index];

      const itemComponents = [
        ...gridItems?.[gridItemIndexKey]?.itemComponents,
        regenerateUniqueKeys(_.cloneDeep(selectedItemComponent)),
      ];

      if (gridItems?.[gridItemIndexKey]?.itemComponents?.length) {
        const updatedGridItemsWithItemComponents = [...gridItems];
        updatedGridItemsWithItemComponents[gridItemIndexKey] = {
          ...gridItems[gridItemIndexKey],
          itemComponents,
        };

        return {
          ...state,
          [parentKey]: {
            ...parent,
            [propsKey]: {
              ...props,
              [gridItemsKey]: [...updatedGridItemsWithItemComponents],
            },
          },
        };
      }

      return {
        ...state,
      };
    }

    case actions.DUPLICATE_DYNAMIC_GRID_OBJECT: {
      const { path } = action;
      // Path example: '592881-DynamicGrid.props.elements.0.elements.0.components.0'
      // We want to duplicate the object/array at the last segment and insert the duplicate right after it

      // Split the path into segments
      const pathArr = path.split('.');

      // The last segment is the index to duplicate
      const lastIndex = pathArr.length - 1;
      const duplicateIndex = parseInt(pathArr[lastIndex], 10);

      // The parent array path is everything up to the last segment
      const parentArrayPathArr = pathArr.slice(0, lastIndex);
      const parentArrayPath = parentArrayPathArr.join('.');

      // Get the parent array
      const parentArray = _.get(state, parentArrayPath);

      // If parentArray is not an array, do nothing
      if (!Array.isArray(parentArray)) {
        return state;
      }

      // Get the item to duplicate
      const itemToDuplicate = parentArray[duplicateIndex];

      // Deep clone the item and regenerate all nested $unique_key values
      const clonedItem = regenerateUniqueKeys(_.cloneDeep(itemToDuplicate));

      // Insert the cloned item right after the original
      const newArray = [
        ...parentArray.slice(0, duplicateIndex + 1),
        clonedItem,
        ...parentArray.slice(duplicateIndex + 1),
      ];

      // Set the new array back into state
      const newState = _.cloneDeep(state);
      _.set(newState, parentArrayPath, newArray);

      // If the root component id is the first segment (e.g., '592881-DynamicGrid'), update that key in state
      const rootComponentId = pathArr[0];
      if (newState[rootComponentId]) {
        return {
          ...state,
          [rootComponentId]: newState[rootComponentId],
        };
      }

      // Otherwise, return the whole new state (should not happen in normal usage)
      return newState;
    }

    case actions.ADD_COMPONENT_TO_DYNAMIC_GRID: {
      const { path, newComponent } = action;
      const currentItem = _.get(state, path);
      const componentId = path.split('.').at(0);
      const gridComponent = state[componentId];

      let componentsList = currentItem?.components;
      if (!componentsList?.length) {
        componentsList = [];
      }
      const foundComponent = componentsList?.find(
        ({ $unique_key }) => $unique_key === newComponent?.$unique_key
      );

      if (foundComponent) {
        return { ...state, [componentId]: gridComponent };
      }
      // Add the new component to the components list
      componentsList.push({ ...newComponent });
      _.set(state, path, {
        ...currentItem,
        components: [...new Set(componentsList)],
      });
      return {
        ...state,
        [componentId]: gridComponent,
      };
    }

    case actions.ADD_CONTAINER_TO_DYNAMIC_GRID: {
      const { path, newContainer } = action;
      const componentId = path.split('.').at(0);
      const gridComponent = state[componentId];
      const currentItem = _.get(state, path);
      const foundContainer = currentItem?.elements?.find(
        ({ $unique_key }) => $unique_key === newContainer?.$unique_key
      );
      if (foundContainer) {
        return { ...state, [componentId]: gridComponent };
      }
      _.set(state, path, {
        ...currentItem,
        elements: [...(currentItem?.elements || []), { ...newContainer }],
      });

      return {
        ...state,
        [componentId]: gridComponent,
      };
    }

    case actions.DELETE_DYNAMIC_GRID_ELEMENT: {
      const { path } = action;
      const componentId = path.split('.').at(0);
      const gridComponent = state[componentId];
      if (!actions._deleteDynamicGridElementProcessed) {
        actions._deleteDynamicGridElementProcessed = new Set();
      }
      const uniqueKey = `${path}`;
      if (actions._deleteDynamicGridElementProcessed.has(uniqueKey)) {
        actions._deleteDynamicGridElementProcessed.delete(uniqueKey);
        return { ...state, [componentId]: gridComponent };
      }
      actions._deleteDynamicGridElementProcessed.add(uniqueKey);

      // Parse the path to extract array path and index
      const pathSegments = path.split('.');
      const lastSegment = pathSegments[pathSegments.length - 1];

      // Check if the last segment is a numeric index (array deletion)
      if (!Number.isNaN(Number(lastSegment))) {
        const arrayPath = pathSegments.slice(0, -1).join('.');
        const indexToDelete = parseInt(lastSegment, 10);
        const currentArray = _.get(state, arrayPath, []);

        // Filter out the element at the specified index
        const updatedArray = currentArray.filter(
          (__, index) => index !== indexToDelete
        );

        // Update the state with the filtered array
        _.set(state, arrayPath, updatedArray);
      } else {
        // For non-array deletions, use unset
        _.unset(state, path);
      }

      return {
        ...state,
        [componentId]: gridComponent,
      };
    }

    case actions.UPDATE_DYNAMIC_GRID_ELEMENT: {
      const { path, element } = action;
      const componentId = path.split('.').at(0);
      const gridComponent = state[componentId];
      _.set(state, path, { ..._.get(state, path), ...element });
      return {
        ...state,
        [componentId]: gridComponent,
      };
    }

    case actions.MOVE_DYNAMIC_GRID_OBJECT: {
      const { path, fromIndex, toIndex } = action;
      const componentId = path.split('.').at(0);

      const gridComponent = state[componentId];
      // Prevent duplicate invocation by using a static Set to track processed actions
      if (!actions._moveDynamicGridObjectProcessed) {
        actions._moveDynamicGridObjectProcessed = new Set();
      }

      // Create a unique key for this move action
      const uniqueKey = `${path}|${fromIndex}|${toIndex}`;
      if (actions._moveDynamicGridObjectProcessed.has(uniqueKey)) {
        // Already processed this action, skip
        actions._moveDynamicGridObjectProcessed.delete(uniqueKey);
        return {
          ...state,
          [componentId]: gridComponent,
        };
      }
      actions._moveDynamicGridObjectProcessed.add(uniqueKey);

      const pathArr = path.split('.');
      const parentPathArr = pathArr.slice(0, -1);
      const parentPath = parentPathArr.join('.');

      const arr = _.get(state, parentPath);
      if (Array.isArray(arr)) {
        // Check if trying to move beyond array boundaries
        const isMovingAboveTop = toIndex < 0;
        const isMovingBelowBottom = toIndex >= arr.length;

        // Prevent movement if trying to move beyond boundaries
        if (isMovingAboveTop || isMovingBelowBottom) {
          return state; // Return current state without changes
        }

        const newArr = moveItem(arr, fromIndex, toIndex);
        _.set(state, parentPath, newArr);
      }

      return {
        ...state,
        [componentId]: gridComponent,
      };
    }

    default:
      return state;

    case actions.MOVE_COMPONENT:
      return moveComponent(action, state);
  }
};

const builderLoaded = (state = false, action) => {
  switch (action.type) {
    case actions.BUILDER_LOADED:
      return true;
    default:
      return state;
  }
};

const selected = (state = '', action) => {
  switch (action.type) {
    case actions.COMPONENT_SELECTED:
      return action.id;
    case actions.COMPONENT_ADDED:
      return action.component.id;
    case actions.COMPONENT_DELETED:
      return action.id === state ? '' : state;
    case actions.EDITOR_CLOSE:
      return '';
    default:
      return state;
  }
};

const builderVersion = (state = '', action) => {
  switch (action.type) {
    case actions.SET_BUILDER_VERSION:
      return action.version;
    default:
      return state;
  }
};

const initialEditor = {
  open: false,
  tab: 0,
  width: 350,
  height: 460,
  dock: 'right',
  showAllMediaViewPorts: false,
  mobileExperienceView: '',
  abTesting: false,
};

const editor = (state = initialEditor, action) => {
  const { tab } = action;
  switch (action.type) {
    case actions.EDITOR_OPEN:
      return {
        ...state,
        open: true,
        abTesting: action.abTesting,
      };
    case actions.TOGGLE_EDITOR:
      return {
        ...state,
        open: !state.open,
      };
    case actions.EDITOR_CLOSE:
    case actions.COMPONENT_DELETED:
      return {
        ...state,
        open: false,
      };
    case actions.EDITOR_TAB_SELECT:
      return {
        ...state,
        tab,
      };
    case actions.COMPONENT_SELECTED:
    case actions.COMPONENT_ADDED:
      return {
        ...state,
        tab: 1,
      };
    case actions.EDITOR_UPDATE_HEIGHT:
      return action.height >= 20
        ? {
            ...state,
            height: action.height,
          }
        : state;
    case actions.EDITOR_UPDATE_WIDTH:
      return {
        ...state,
        width: action.width,
      };
    case actions.EDITOR_UPDATE_DOCK:
      return action.dock === state.dock
        ? {
            ...state,
            open: false,
          }
        : {
            ...state,
            dock: action.dock,
          };

    case actions.EDITOR_SHOW_ALL_VIEW_PORTS:
      return {
        ...state,
        showAllMediaViewPorts: action.payload,
      };
    case actions.EDITOR_TOGGLE_ALL_VIEW_PORTS:
      return {
        ...state,
        showAllMediaViewPorts: !state.showAllMediaViewPorts,
      };
    case actions.EDITOR_CHANGE_MOBILE_EXPERIENCE_VIEW:
      return {
        ...state,
        mobileExperienceView: action.payload,
      };
    default:
      return state;
  }
};

const pageMapInitialState = {
  open: false,
  width: 350,
  height: 460,
  dock: 'left',
  hoveredComponentId: '',
};

const pageMap = (state = pageMapInitialState, action) => {
  switch (action.type) {
    case actions.OPEN_PAGE_MAP:
      return {
        ...state,
        open: true,
      };
    case actions.TOGGLE_PAGE_MAP:
      return {
        ...state,
        open: !state.open,
      };
    case actions.CLOSE_PAGE_MAP:
      return {
        ...state,
        open: false,
      };
    case actions.UPDATE_PAGE_MAP_WIDTH:
      return {
        ...state,
        width: action.width,
      };
    default:
      return state;
  }
};

const badges = (state = {}, action) => {
  switch (action.type) {
    case actions.OUTLINE_RECEIVED:
      return treeIntoFlags(action.outline);
    case actions.COMPONENT_ADDED:
      return {
        ...state,
        [action.component.id]: true,
      };
    case actions.TOGGLE_BADGE:
      return {
        ...state,
        [action.componentId]: !state[action.componentId],
      };
    case actions.CLOSE_ALL_BADGES:
      return {};
    default:
      return state;
  }
};

const watchQueryParams = (state = false, action) => {
  switch (action.type) {
    case actions.SHOULD_WATCH_QUERY_PARAMS:
      return action.watchQueryParams;
    default:
      return state;
  }
};

const alwaysRenderInBuilder = (state = [], action) => {
  switch (action.type) {
    case actions.ALWAYS_RENDER_IN_BUILDER:
      return state.concat([action.id]);
    default:
      return state;
  }
};

const sorting = (state = {}, action) => {
  switch (action.type) {
    case actions.START_SORTING:
      return {
        ...state,
        componentId: action.componentId,
      };
    case actions.STOP_SORTING:
      return {
        ...state,
        componentId: undefined,
      };
    case actions.MOVE_COMPONENT:
      return {
        ...state,
        componentId: undefined,
      };
    default:
      return state;
  }
};

const dynamicGridInitialState = {
  componentPath: '',
  componentType: '',
  disableGridOutline: [],
  activeElementId: null,
  marketplaceOffers: {},
};

const dynamicGrid = (state = dynamicGridInitialState, action) => {
  switch (action.type) {
    case actions.SET_COMPONENT_PATH:
      return {
        ...state,
        componentPath: action.path,
      };
    case actions.SET_COMPONENT_TYPE:
      return {
        ...state,
        componentType: action.componentType,
      };
    case actions.SET_MARKETPLACE_OFFERS:
      return {
        ...state,
        marketplaceOffers: {
          ...state.marketplaceOffers,
          [action.componentId]: action.marketplaceOffers,
        },
      };
    case actions.SET_DISABLE_GRID_OUTLINE:
      return {
        ...state,
        disableGridOutline: {
          ...state.disableGridOutline,
          [action.payload.componentId]: action.payload.disableGridOutline,
        },
      };
    case actions.SET_ACTIVE_ELEMENT_ID:
      return {
        ...state,
        activeElementId: action.payload,
      };
    default:
      return state;
  }
};

export default combineReducers({
  flatOutline,
  builderLoaded,
  selected,
  editor,
  badges,
  watchQueryParams,
  alwaysRenderInBuilder,
  sorting,
  version: builderVersion,
  dynamicGrid,
  pageMap,
});
